/**
 * Cross-platform install prompt.
 *
 * Detects the user's platform and surfaces the right install affordance:
 *   - Android Chrome / Desktop Chrome / Desktop Edge → captures `beforeinstallprompt`
 *     and shows an Install button (single tap; native browser dialog).
 *   - iOS Safari (iPhone/iPad) → manual instructions ("Share → Add to Home Screen")
 *     because Apple doesn't fire `beforeinstallprompt`.
 *   - Mac Safari (16.4+) → manual instructions ("File → Add to Dock") if not standalone.
 *   - Already installed (display-mode: standalone | minimal-ui | window-controls-overlay)
 *     → renders nothing.
 *
 * The component supports two render modes:
 *   - inline `mode="banner"` (default): used on Dashboard. Auto-shows once, dismissible
 *     to localStorage, hidden after dismissal.
 *   - `mode="button"`: explicit button you can place anywhere (e.g. Profile) so a user
 *     who dismissed the banner can re-trigger the install flow at will.
 */
import React, { useEffect, useState, useCallback } from "react";
import { X, Plus, Share, Download, Smartphone, Monitor } from "lucide-react";

const DISMISSED_KEY = "gwg_install_dismissed_v1";

// Module-level cache of the deferred prompt event so any mounted instance can use it.
let _deferredPrompt = null;
const _listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _listeners.forEach((fn) => fn(e));
  });
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _listeners.forEach((fn) => fn(null));
  });
}

function detectPlatform() {
  if (typeof window === "undefined") return "unknown";
  const ua = window.navigator.userAgent;
  const isStandalone =
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches);
  if (isStandalone) return "installed";

  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ pretends to be Mac; detect via touch-points.
  const isIPadOS = /Mac/.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  if (isIOS || isIPadOS) return "ios";

  const isAndroid = /android/i.test(ua);
  if (isAndroid) return "android";

  // Desktop. Distinguish Chrome/Edge (BIP fires) from Safari (manual) and Firefox (no install).
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge && !/Chromium/.test(ua);
  const isSafariMac = /^((?!chrome|android|edg).)*safari/i.test(ua) && /Mac OS X/.test(ua);
  if (isEdge) return "desktop-edge";
  if (isChrome) return "desktop-chrome";
  if (isSafariMac) return "mac-safari";
  return "desktop-other";
}

export default function InstallPrompt({ mode = "banner", className = "" }) {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState("unknown");
  const [hasNativePrompt, setHasNativePrompt] = useState(!!_deferredPrompt);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === "installed") return;

    // Banner mode auto-shows for first-time users on platforms that can install.
    if (mode === "banner") {
      const dismissed = window.localStorage.getItem(DISMISSED_KEY) === "1";
      const canInstall = p !== "installed" && p !== "desktop-other";
      if (!dismissed && canInstall) setShow(true);
    } else {
      setShow(true);
    }

    const listener = (event) => setHasNativePrompt(!!event);
    _listeners.add(listener);
    return () => { _listeners.delete(listener); };
  }, [mode]);

  const dismiss = useCallback(() => {
    if (mode === "banner") window.localStorage.setItem(DISMISSED_KEY, "1");
    setShow(false);
  }, [mode]);

  const triggerNativeInstall = useCallback(async () => {
    if (!_deferredPrompt) return false;
    try {
      _deferredPrompt.prompt();
      const choice = await _deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        _deferredPrompt = null;
        setHasNativePrompt(false);
        if (mode === "banner") dismiss();
        return true;
      }
    } catch {
      /* user cancelled */
    }
    return false;
  }, [dismiss, mode]);

  // On Android Chrome the BIP event sometimes fires before this component mounts;
  // we surface a button as soon as the cached _deferredPrompt is available.
  if (platform === "installed") return null;
  if (mode === "banner" && !show) return null;

  // Manual-instruction renderer for iOS / Mac Safari / Firefox / unsupported.
  const ManualInstructions = () => {
    if (platform === "ios") {
      return (
        <div className="text-xs text-neutral-300 mt-1 space-y-0.5">
          <div>1. Tap <Share className="inline w-3 h-3 align-middle text-blue-500" /> in Safari's bottom toolbar.</div>
          <div>2. Scroll and tap <span className="text-blue-500 font-semibold">Add to Home Screen</span>.</div>
          <div>3. Tap <span className="text-blue-500 font-semibold">Add</span>.</div>
        </div>
      );
    }
    if (platform === "mac-safari") {
      return (
        <div className="text-xs text-neutral-300 mt-1 space-y-0.5">
          <div>1. In Safari menu: <span className="text-blue-500 font-semibold">File → Add to Dock</span> (macOS Sonoma+).</div>
          <div>2. Pick a name, click <span className="text-blue-500 font-semibold">Add</span>.</div>
        </div>
      );
    }
    if (platform === "android") {
      return (
        <div className="text-xs text-neutral-300 mt-1 space-y-0.5">
          <div>1. Tap the browser menu (⋮ top-right).</div>
          <div>2. Tap <span className="text-blue-500 font-semibold">Install app</span> or <span className="text-blue-500 font-semibold">Add to Home screen</span>.</div>
        </div>
      );
    }
    if (platform === "desktop-chrome" || platform === "desktop-edge") {
      return (
        <div className="text-xs text-neutral-300 mt-1 space-y-0.5">
          <div>1. Click the install icon (⊕) in the address bar — or open the menu (⋮) → <span className="text-blue-500 font-semibold">Install GYM with GRIZZ…</span></div>
          <div>2. Click <span className="text-blue-500 font-semibold">Install</span>.</div>
        </div>
      );
    }
    return (
      <div className="text-xs text-neutral-300 mt-1">
        Use Chrome, Edge, or Safari to install this app on your device.
      </div>
    );
  };

  // Button mode (Profile) — always renders a chip you can tap.
  if (mode === "button") {
    return (
      <div data-testid="install-button-card" className={`border border-neutral-800 p-3 ${className}`}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-blue-500 flex items-center justify-center shrink-0">
            {platform === "ios" || platform === "android" ? <Smartphone className="w-5 h-5 text-white" /> : <Monitor className="w-5 h-5 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm uppercase tracking-wide">Install GYM with GRIZZ</div>
            <div className="text-xs text-neutral-400 mt-0.5">Pin to your home screen / dock for full-screen, faster access.</div>
            {hasNativePrompt ? (
              <button
                data-testid="install-trigger-btn"
                onClick={triggerNativeInstall}
                className="mt-2 px-3 h-9 inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white text-xs uppercase tracking-widest font-display"
              >
                <Download className="w-3 h-3 mr-1.5" /> Install
              </button>
            ) : (
              <button
                data-testid="install-help-toggle"
                onClick={() => setShowHelp((v) => !v)}
                className="mt-2 px-3 h-9 inline-flex items-center border border-neutral-700 text-neutral-200 hover:border-blue-500 text-xs uppercase tracking-widest font-display"
              >
                {showHelp ? "Hide" : "How to install"}
              </button>
            )}
            {showHelp && <div data-testid="install-help-text" className="mt-2"><ManualInstructions /></div>}
          </div>
        </div>
      </div>
    );
  }

  // Banner mode (Dashboard).
  return (
    <div data-testid="install-prompt" className={`border border-blue-500/50 bg-blue-500/5 p-3 mb-4 flex items-start gap-3 ${className}`}>
      <div className="w-9 h-9 bg-blue-500 flex items-center justify-center shrink-0">
        <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-sm uppercase tracking-wide">Add to Home Screen</div>
        {hasNativePrompt ? (
          <>
            <div className="text-xs text-neutral-300 mt-0.5">Install GYM with GRIZZ as an app — full-screen, faster.</div>
            <button
              data-testid="install-native-btn"
              onClick={triggerNativeInstall}
              className="mt-2 text-xs uppercase tracking-widest text-blue-500 font-bold inline-flex items-center"
            >
              <Download className="w-3 h-3 mr-1" /> Install →
            </button>
          </>
        ) : (
          <ManualInstructions />
        )}
      </div>
      <button data-testid="install-dismiss" onClick={dismiss} className="text-neutral-500 hover:text-white shrink-0">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
